/**
 * First-touch attribution, carried across the pocketsflow.com subdomains.
 *
 * The signup funnel spans three hosts — the marketing site
 * (www.pocketsflow.com), creator storefronts (<creator>.pocketsflow.com) and
 * the app (app.pocketsflow.com) — but the `signup_completed` event is emitted
 * SERVER-side, keyed on the Auth0 user id. Server events can't see the
 * browser's UTM params or referrer, so without this the acquisition source of
 * every signup is unknowable (POC-538).
 *
 * Fix: the first page a visitor lands on writes a first-touch record to a
 * cookie scoped to `.pocketsflow.com`. The cookie survives the hop to
 * app.pocketsflow.com, `/callback` reads it at signup time and posts it with
 * the new user, and the backend stamps it onto `signup_completed`.
 *
 * First touch WINS: once written the record is never overwritten, so a visitor
 * who arrives from a Threads campaign, leaves, and comes back direct is still
 * credited to Threads.
 *
 * Custom creator domains (not *.pocketsflow.com) can't share a cookie with the
 * app — those land as `source: "direct"`, which is the same as today.
 *
 * The app records it at boot (src/index.tsx), before Auth0 can redirect: it
 * used to be recorded only when the analytics tag loaded, which for a visitor
 * landing on app.pocketsflow.com was after signing in, so the source came out
 * as the sign-in page or "direct" (2026-11: 76% of creator signups "direct").
 */

export const ATTRIBUTION_COOKIE = "pf_attr";

/** 90 days — long enough to cover a slow consider-then-signup path. */
const MAX_AGE_SECONDS = 90 * 24 * 60 * 60;

/** Cookies have a hard 4KB budget; keep every field well inside it. */
const MAX_FIELD_LENGTH = 200;

/** Which funnel the visitor entered through. */
export type Surface = "marketing" | "storefront" | "app";

export type Attribution = {
	/** utm_source, else the referring domain, else "direct". */
	source: string;
	medium: string;
	campaign: string;
	content: string;
	term: string;
	/** Full referrer URL, truncated. */
	referrer: string;
	/** Host + path of the first page seen — e.g. which storefront. */
	landingHost: string;
	landingPath: string;
	landingSurface: Surface;
	firstSeenAt: string;
};

const truncate = (value: string | null | undefined): string =>
	(value || "").slice(0, MAX_FIELD_LENGTH);

/**
 * Cookie domain that both the entry host and the app can read.
 *
 * `.pocketsflow.com` covers www, every creator subdomain and the app. Anything
 * else (custom domains, localhost, preview URLs) gets a host-only cookie — it
 * still works within that host, it just can't follow the visitor to the app.
 */
/** Shared by `pf_attr` and partner referral (`pf_partner_ref`). */
export const firstPartyCookieDomain = (hostname: string): string =>
	hostname === "pocketsflow.com" || hostname.endsWith(".pocketsflow.com")
		? "; domain=.pocketsflow.com"
		: "";

const isOwnHost = (host: string): boolean =>
	host === "pocketsflow.com" || host.endsWith(".pocketsflow.com");

/**
 * Our own hosts that are not a creator's storefront. Keep in step with
 * `apps/landing-new/src/lib/attribution.ts`.
 */
const FIRST_PARTY_LABELS = new Set([
	"www",
	"app",
	"api",
	"docs",
	"admin",
	"status",
	"cdn",
	"help",
	"mcp",
	"blog",
	"staging",
]);

/**
 * A creator storefront (`<creator>.pocketsflow.com`) that sent the visitor:
 * a buyer who clicked through to start their own store. That is the
 * buyer→creator loop, so it is a source ("storefront"), not internal traffic.
 */
export const isStorefrontHost = (host: string): boolean =>
	host.endsWith(".pocketsflow.com") &&
	!FIRST_PARTY_LABELS.has(host.split(".")[0]);

/**
 * Sign-in pages (Auth0, Google, Apple, Microsoft) are a step in the signup,
 * not where the visitor came from. Before this they showed up as sources
 * ("castflow.us.auth0.com", "accounts.google.com") for anyone whose first
 * touch was only recorded after logging in.
 */
export const isAuthHost = (host: string): boolean =>
	host.endsWith(".auth0.com") ||
	host === "accounts.google.com" ||
	host === "appleid.apple.com" ||
	host === "login.microsoftonline.com" ||
	host === "login.live.com";

/** The source a referring host stands for, or "" when it isn't one. */
export const referrerSource = (host: string): string => {
	if (!host) return "";
	if (isStorefrontHost(host)) return "storefront";
	if (isOwnHost(host) || isAuthHost(host)) return "";
	return host;
};

export const readAttribution = (): Attribution | null => {
	try {
		const match = document.cookie.match(
			new RegExp(`(?:^|; )${ATTRIBUTION_COOKIE}=([^;]*)`),
		);
		if (!match) return null;
		const parsed = JSON.parse(decodeURIComponent(match[1]));
		return parsed && typeof parsed === "object"
			? (parsed as Attribution)
			: null;
	} catch {
		// Malformed/foreign cookie — attribution is best-effort, never fatal.
		return null;
	}
};

/**
 * Record the first touch, if there isn't one already.
 *
 * Safe to call on every page load: it no-ops once the cookie exists.
 */
export const captureAttribution = (landingSurface: Surface): void => {
	try {
		if (readAttribution()) return;

		const params = new URLSearchParams(window.location.search);
		const referrer = document.referrer || "";
		let referringDomain = "";
		try {
			referringDomain = referrer ? new URL(referrer).hostname : "";
		} catch {
			referringDomain = "";
		}

		// Our own pages and sign-in pages aren't a source — a visitor who
		// clicked through from another Pocketsflow page is still "direct"
		// unless tagged. A creator storefront is (the buyer→creator loop).
		const attribution: Attribution = {
			source: truncate(
				params.get("utm_source") ||
					params.get("ref") ||
					referrerSource(referringDomain) ||
					"direct",
			),
			medium: truncate(params.get("utm_medium")),
			campaign: truncate(params.get("utm_campaign")),
			content: truncate(params.get("utm_content")),
			term: truncate(params.get("utm_term")),
			referrer: truncate(referrer),
			landingHost: truncate(window.location.hostname),
			landingPath: truncate(window.location.pathname),
			landingSurface,
			firstSeenAt: new Date().toISOString(),
		};

		// The Cookie Store API is unsupported in Safari, and this must be written
		// synchronously with an explicit `domain=.pocketsflow.com` on the very
		// first page — an async write can lose the race with a click into the app.
		// biome-ignore lint/suspicious/noDocumentCookie: needs sync cross-subdomain write
		document.cookie = `${ATTRIBUTION_COOKIE}=${encodeURIComponent(
			JSON.stringify(attribution),
		)}; path=/${firstPartyCookieDomain(
			window.location.hostname,
		)}; max-age=${MAX_AGE_SECONDS}; SameSite=Lax`;
	} catch {
		// Cookies disabled / storage blocked — attribution is best-effort.
	}
};
